/**
 * What each block of a page, and the site settings, are made of (ADR 0081): one description per block type drives the editor's
 * fields. The server checks the same shapes when a draft is saved (Pos.Cms BlockTypes); a new block type is a line here, a class
 * there and a component on the website.
 */

export type FieldKind =
  | 'text'
  | 'richtext'
  | 'plain'
  | 'image'
  | 'link'
  | 'number'
  | 'select'
  | 'multi'
  | 'boolean'
  | 'category'
  | 'deal'
  | 'form'
  | 'list'
  | 'group';

export interface ContentField {
  key: string;
  kind: FieldKind;
  required?: boolean;
  /** A list's or group's own fields. */
  fields?: readonly ContentField[];
  /** A select's or multi's choices (codes; labels in `cms.options.<key>.<code>`). */
  options?: readonly string[];
  /** A multi whose codes are numbers (the days of the week). */
  numeric?: boolean;
  max?: number;
}

const title: ContentField = { key: 'title', kind: 'text' };

export const BLOCK_TYPES: Readonly<Record<string, readonly ContentField[]>> = {
  hero: [
    {
      key: 'slides',
      kind: 'list',
      max: 8,
      fields: [
        { key: 'image', kind: 'image', required: true },
        { key: 'imageNarrow', kind: 'image' },
        title,
        { key: 'text', kind: 'text' },
        { key: 'buttonLabel', kind: 'text' },
        { key: 'href', kind: 'link' },
      ],
    },
  ],
  order_starter: [],
  rich_text: [{ key: 'text', kind: 'richtext', required: true }],
  image: [
    { key: 'image', kind: 'image', required: true },
    { key: 'alt', kind: 'text', required: true },
    { key: 'caption', kind: 'text' },
    { key: 'href', kind: 'link' },
  ],
  tiles: [
    title,
    {
      key: 'tiles',
      kind: 'list',
      max: 12,
      fields: [
        { key: 'image', kind: 'image', required: true },
        { key: 'title', kind: 'text', required: true },
        { key: 'href', kind: 'link', required: true },
      ],
    },
  ],
  cta: [
    { key: 'title', kind: 'text', required: true },
    { key: 'text', kind: 'text' },
    { key: 'buttonLabel', kind: 'text', required: true },
    { key: 'href', kind: 'link', required: true },
  ],
  featured_items: [title, { key: 'categoryId', kind: 'category' }, { key: 'limit', kind: 'number' }],
  deals_row: [title, { key: 'limit', kind: 'number' }],
  store_list: [title],
  app_download: [title, { key: 'text', kind: 'text' }],
  // Questions and answers (CMS phase 2): the FAQ page (slug "faq") is made of these.
  faq: [
    title,
    {
      key: 'items',
      kind: 'list',
      max: 50,
      fields: [
        { key: 'question', kind: 'text', required: true },
        { key: 'answer', kind: 'richtext', required: true },
      ],
    },
  ],
  // A form made under CMS › Forms (CMS phase 2), placed on the page; a heading and text may lead in.
  form: [{ key: 'formId', kind: 'form', required: true }, title, { key: 'text', kind: 'richtext' }],
};

/** The kinds of form field the website shows and its API checks (Pos.Cms BlockTypes.FieldKinds). */
export const FORM_FIELD_KINDS = ['text', 'email', 'phone', 'textarea', 'number', 'date', 'select', 'checkbox'] as const;

/** A form's content (a page of kind form): the server checks the same (Pos.Cms BlockTypes.ValidateForm). */
export const FORM_FIELDS: readonly ContentField[] = [
  {
    key: 'fields',
    kind: 'list',
    max: 20,
    fields: [
      { key: 'key', kind: 'plain', required: true },
      { key: 'label', kind: 'text', required: true },
      { key: 'kind', kind: 'select', required: true, options: FORM_FIELD_KINDS },
      { key: 'required', kind: 'boolean' },
      { key: 'help', kind: 'text' },
      {
        key: 'options',
        kind: 'list',
        max: 30,
        fields: [
          { key: 'value', kind: 'plain', required: true },
          { key: 'label', kind: 'text', required: true },
        ],
      },
    ],
  },
  { key: 'submitLabel', kind: 'text' },
  { key: 'successText', kind: 'text' },
  { key: 'consent', kind: 'richtext' },
];

/** A promotion page's details (CMS phase 2): the server checks the same (Pos.Cms BlockTypes.ValidatePromotion). */
export const PROMOTION_FIELDS: readonly ContentField[] = [
  { key: 'type', kind: 'text' },
  { key: 'days', kind: 'multi', numeric: true, options: ['1', '2', '3', '4', '5', '6', '0'] },
  {
    key: 'hours',
    kind: 'group',
    fields: [
      { key: 'from', kind: 'number' },
      { key: 'to', kind: 'number' },
    ],
  },
  { key: 'channels', kind: 'multi', options: ['delivery', 'pickup', 'dine_in'] },
  { key: 'dealId', kind: 'deal' },
  { key: 'orderHref', kind: 'link' },
  { key: 'terms', kind: 'richtext' },
];

const link: readonly ContentField[] = [
  { key: 'label', kind: 'text', required: true },
  { key: 'href', kind: 'link', required: true },
];

export const SITE_FIELDS: readonly ContentField[] = [
  { key: 'headerLinks', kind: 'list', max: 12, fields: link },
  {
    key: 'footerColumns',
    kind: 'list',
    max: 6,
    fields: [
      { key: 'title', kind: 'text', required: true },
      { key: 'links', kind: 'list', max: 12, fields: link },
    ],
  },
  {
    key: 'social',
    kind: 'list',
    max: 10,
    fields: [
      {
        key: 'network',
        kind: 'select',
        required: true,
        options: ['facebook', 'instagram', 'tiktok', 'youtube', 'zalo', 'x'],
      },
      { key: 'href', kind: 'link', required: true },
    ],
  },
  {
    key: 'apps',
    kind: 'group',
    fields: [
      { key: 'ios', kind: 'link' },
      { key: 'android', kind: 'link' },
    ],
  },
  {
    key: 'legal',
    kind: 'group',
    fields: [
      { key: 'companyName', kind: 'plain' },
      { key: 'address', kind: 'plain' },
      { key: 'registration', kind: 'text' },
      { key: 'taxCode', kind: 'plain' },
      { key: 'hotline', kind: 'plain' },
      { key: 'email', kind: 'plain' },
    ],
  },
];

export type Json = Record<string, unknown>;

/** A content field's JSON as an object; anything else as empty. */
export function parseContent(value: unknown): Json {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Json;
  if (typeof value !== 'string' || !value.trim()) return {};
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Json) : {};
  } catch {
    return {};
  }
}

/** A new block of a type, with an id unique on the page. */
export function newBlock(type: string, taken: readonly string[]): Json {
  let n = 1;
  while (taken.includes(`${type.replace('_', '-')}-${n}`)) n++;
  return { id: `${type.replace('_', '-')}-${n}`, type };
}

/** The list with item `from` moved to `to`. */
export function move<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  if (to < 0 || to >= next.length) return next;
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}
